/**
 * LOCALE Web Analytics — unified service wrapping all 6 tools:
 *   Sentry       (crash / error tracking)
 *   PostHog      (product analytics)
 *   Mixpanel     (funnel tracking)
 *   Firebase     (GA4 analytics)
 *   LogRocket    (session replay)
 *   Datadog      (RUM + infrastructure monitoring)
 *
 * Every service is opt-in: it only activates when the corresponding
 * VITE_* environment variable is present. The app works normally without
 * any keys configured.
 *
 * Bundle strategy — ALL heavy SDKs are loaded via dynamic import():
 *   Static imports cost bundle bytes on every page load, even when the
 *   corresponding env var is absent. Dynamic imports let Vite split each SDK
 *   into its own async chunk that is only downloaded when the env var is set.
 *   Sentry (~100 KB gz), Mixpanel (~50 KB gz), and PostHog (~50 KB gz) are the
 *   main savings — they were previously static and landing in index.js.
 *
 * Required env vars (all optional):
 *   VITE_SENTRY_DSN
 *   VITE_POSTHOG_API_KEY
 *   VITE_POSTHOG_HOST           (default: https://app.posthog.com)
 *   VITE_MIXPANEL_TOKEN
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_APP_ID
 *   VITE_FIREBASE_MEASUREMENT_ID
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_LOGROCKET_APP_ID
 *   VITE_DATADOG_CLIENT_TOKEN
 *   VITE_DATADOG_APPLICATION_ID
 *   VITE_DATADOG_SERVICE        (default: locale-web)
 */

// ─── Event catalogue ─────────────────────────────────────────────────────────

export const Events = {
  // Auth
  USER_SIGNED_UP:           "user_signed_up",
  USER_SIGNED_IN:           "user_signed_in",
  USER_SIGNED_OUT:          "user_signed_out",
  USER_DELETED_ACCOUNT:     "user_deleted_account",
  // Waitlist
  WAITLIST_JOINED:          "waitlist_joined",
  // Marketing
  PAGE_VIEWED:              "page_viewed",
  CTA_CLICKED:              "cta_clicked",
  PRICING_VIEWED:           "pricing_viewed",
  SUBSCRIPTION_STARTED:     "subscription_started",
  SUBSCRIPTION_CANCELLED:   "subscription_cancelled",
  // Auth modal
  AUTH_MODAL_OPENED:        "auth_modal_opened",
  AUTH_FORM_SUBMITTED:      "auth_form_submitted",
  // Reset password
  RESET_PASSWORD_REQUESTED: "reset_password_requested",
  RESET_PASSWORD_COMPLETED: "reset_password_completed",
  // Contact
  CONTACT_FORM_SUBMITTED:   "contact_form_submitted",
} as const;

export type EventName = (typeof Events)[keyof typeof Events];
type Properties = Record<string, unknown>;

// ─── Lazy SDK handles ─────────────────────────────────────────────────────────
// Populated inside the corresponding init* functions — never at module load time.
// Using `any` here is intentional: the SDK types are complex union types that
// are only accessible after the dynamic import resolves, and they're internal
// to this module. All public-API functions are well-typed.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _sentry: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _mixpanel: any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _posthog: any;

// ─── Service state ────────────────────────────────────────────────────────────

let posthogReady = false;
let mixpanelReady = false;
let sentryReady = false;
let logRocketReady = false;
let datadogReady = false;
let firebaseAnalytics: import("firebase/analytics").Analytics | null = null;

// ─── Initialisation ───────────────────────────────────────────────────────────

export async function initAnalytics(): Promise<void> {
  await Promise.all([
    initSentry(),
    initPostHog(),
    initMixpanel(),
    initFirebase(),
    initLogRocket(),
    initDatadog(),
  ]);
}

async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  try {
    // Dynamic import — Sentry (~100 KB gz) only downloaded when DSN is set
    _sentry = await import("@sentry/react");
    _sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: "locale-web@1.0.0",
      integrations: [
        _sentry.browserTracingIntegration(),
        _sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: true,
        }),
      ],
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event: Record<string, unknown>) {
        // Strip password fields from captured requests
        const req = event.request as { data?: Record<string, unknown> } | undefined;
        if (req?.data?.password) req.data.password = "[REDACTED]";
        return event;
      },
    });
    sentryReady = true;
  } catch (err) {
    console.warn("[LOCALE Analytics] Sentry init failed:", err);
  }
}

async function initPostHog(): Promise<void> {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey) return;
  try {
    // Dynamic import — PostHog (~50 KB gz) only downloaded when API key is set
    const mod = await import("posthog-js");
    _posthog = mod.default;
    _posthog.init(apiKey, {
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string) ?? "https://app.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      autocapture: {
        dom_event_allowlist: ["click"],
        element_allowlist: ["button", "a"],
        css_selector_allowlist: ["[data-ph-capture]"],
      },
    });
    posthogReady = true;
  } catch (err) {
    console.warn("[LOCALE Analytics] PostHog init failed:", err);
  }
}

async function initMixpanel(): Promise<void> {
  const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
  if (!token) return;
  try {
    // Dynamic import — Mixpanel (~50 KB gz) only downloaded when token is set
    const mod = await import("mixpanel-browser");
    _mixpanel = mod.default;
    _mixpanel.init(token, {
      debug: import.meta.env.DEV,
      track_pageview: "url-with-path",
      persistence: "localStorage",
      batch_requests: true,
      batch_flush_interval_ms: 5_000,
      ignore_dnt: false,
      record_sessions_percent: import.meta.env.PROD ? 5 : 0,
    });
    mixpanelReady = true;
  } catch (err) {
    console.warn("[LOCALE Analytics] Mixpanel init failed:", err);
  }
}

async function initFirebase(): Promise<void> {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID as string | undefined;
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  if (!apiKey || !appId || !projectId) return;
  try {
    const { initializeApp, getApps } = await import("firebase/app");
    const { getAnalytics, isSupported } = await import("firebase/analytics");
    const supported = await isSupported();
    if (!supported) return;
    const app =
      getApps().length === 0
        ? initializeApp({ apiKey, appId, projectId, measurementId })
        : getApps()[0]!;
    firebaseAnalytics = getAnalytics(app);
  } catch (err) {
    console.warn("[LOCALE Analytics] Firebase init failed:", err);
  }
}

async function initLogRocket(): Promise<void> {
  const appId = import.meta.env.VITE_LOGROCKET_APP_ID as string | undefined;
  if (!appId || !import.meta.env.PROD) return;
  try {
    const [LogRocket, { default: setupLogRocketReact }] = await Promise.all([
      import("logrocket").then((m) => m.default),
      import("logrocket-react"),
    ]);
    LogRocket.init(appId, {
      release: "1.0.0",
      console: { isEnabled: { log: false, debug: false } },
      network: {
        requestSanitizer: (request) => {
          if (request.url?.includes("/auth") || request.url?.includes("/password")) {
            if (request.body) {
              try {
                const body = JSON.parse(request.body) as Record<string, unknown>;
                if (body.password) body.password = "[REDACTED]";
                if (body.token) body.token = "[REDACTED]";
                request.body = JSON.stringify(body);
              } catch {}
            }
          }
          return request;
        },
      },
    });
    // logrocket-react adds React component display names to session replays.
    (setupLogRocketReact as (lr: unknown) => void)(LogRocket);
    logRocketReady = true;

    // Bridge LogRocket sessions into Sentry
    if (sentryReady && _sentry) {
      LogRocket.getSessionURL((sessionURL: string) => {
        _sentry.setContext("logrocket", { sessionURL });
      });
    }
  } catch (err) {
    console.warn("[LOCALE Analytics] LogRocket init failed:", err);
  }
}

async function initDatadog(): Promise<void> {
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN as string | undefined;
  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID as string | undefined;
  if (!clientToken) return;
  try {
    const [{ datadogRum }, { datadogLogs }] = await Promise.all([
      import("@datadog/browser-rum"),
      import("@datadog/browser-logs"),
    ]);

    const service = (import.meta.env.VITE_DATADOG_SERVICE as string) ?? "locale-web";

    datadogLogs.init({
      clientToken,
      site: "datadoghq.com",
      service,
      env: import.meta.env.MODE,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error", "warn"],
      sessionSampleRate: 100,
    });

    if (applicationId) {
      datadogRum.init({
        applicationId,
        clientToken,
        site: "datadoghq.com",
        service,
        env: import.meta.env.MODE,
        version: "1.0.0",
        sessionSampleRate: 100,
        sessionReplaySampleRate: import.meta.env.PROD ? 10 : 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
      });
    }
    datadogReady = true;
  } catch (err) {
    console.warn("[LOCALE Analytics] Datadog init failed:", err);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function identify(userId: string, traits?: Properties): void {
  try {
    if (sentryReady && _sentry) _sentry.setUser({ id: userId, email: traits?.email as string | undefined, ...traits });
  } catch {}

  try {
    if (posthogReady && _posthog) _posthog.identify(userId, traits);
  } catch {}

  try {
    if (mixpanelReady && _mixpanel) {
      _mixpanel.identify(userId);
      if (traits) _mixpanel.people.set(traits);
    }
  } catch {}

  try {
    if (logRocketReady) {
      import("logrocket").then(({ default: LogRocket }) => {
        LogRocket.identify(userId, traits as Record<string, string | number | boolean> | undefined);
      });
    }
  } catch {}
}

export function track(event: EventName | string, properties?: Properties): void {
  try {
    if (posthogReady && _posthog) _posthog.capture(event, properties);
  } catch {}

  try {
    if (mixpanelReady && _mixpanel) _mixpanel.track(event, properties);
  } catch {}

  try {
    if (firebaseAnalytics) {
      import("firebase/analytics").then(({ logEvent }) => {
        logEvent(firebaseAnalytics!, event.replace(/[^a-zA-Z0-9_]/g, "_"), properties);
      });
    }
  } catch {}
}

export function page(name?: string, properties?: Properties): void {
  const pageName = name ?? document.title;
  const props = {
    page: pageName,
    url: window.location.href,
    path: window.location.pathname,
    referrer: document.referrer,
    ...properties,
  };

  track(Events.PAGE_VIEWED, props);

  try {
    if (posthogReady && _posthog) _posthog.capture("$pageview", props);
  } catch {}

  try {
    if (mixpanelReady && _mixpanel) _mixpanel.track_pageview(props);
  } catch {}
}

export function reset(): void {
  try {
    if (sentryReady && _sentry) _sentry.setUser(null);
  } catch {}
  try {
    if (posthogReady && _posthog) _posthog.reset();
  } catch {}
  try {
    if (mixpanelReady && _mixpanel) _mixpanel.reset();
  } catch {}
}

export function captureException(error: Error, context?: Properties): void {
  try {
    // _sentry may be undefined if Sentry is not configured — safe no-op
    if (sentryReady && _sentry) _sentry.captureException(error, { extra: context });
  } catch {}
}
